import React, { useState, useEffect, useCallback, useRef } from 'react';
import { STRRoom, STRBooking } from './types';
import { formatDate } from './utils';
import { API_BASE_URL } from '../config';
import { useResponsive } from '../hooks/useResponsive';
import RoomList from './RoomList';
import Calendar from './Calendar';
import Sidebar from './Sidebar';
import SettlementDashboard from './SettlementDashboard';
import BookingList from './BookingList';
import RoomModal from './modals/RoomModal';
import BookingModal from './modals/BookingModal';
import BlockModal from './modals/BlockModal';
import MessageModal from './modals/MessageModal';
import CancelModal from './modals/CancelModal';
import CoManageModal from './modals/CoManageModal';

interface RoomManagementProps {
  token: string;
  user: { id: string; name: string; email: string };
}

const DEMO_ROOM: STRRoom = {
  id: '__demo__',
  user_id: '',
  name: '신영지웰 1002호',
  address: '서울 강서구 공항대로 지하 287',
  road_addr: '서울 강서구 공항대로 지하 287',
  jibun_addr: '서울 강서구 염창동 273',
  zipcode: '07547',
  building_name: '신영지웰시티 1차',
  detail_address: '1002호',
  room_type: '오피스텔',
  area_m2: '24.5',
  floor: '10',
  description: '예시 매물입니다.',
  weekly_rent: 280000,
  weekly_maintenance: 80000,
  weekly_cleaning: 30000,
  deposit: 330000,
  monthly_rent: 0,
  monthly_maintenance: 0,
  monthly_cleaning: 0,
  monthly_deposit: 0,
  min_days: 7,
  max_days: 365,
  status: 'active',
  ownership_type: '전대 동의',
  contract_start: '2026-01-01',
  contract_end: '2027-01-31',
  business_type: '개인',
  operation_start: '2026-01-05',
  sublease_deposit: 0,
  sublease_rent: 650000,
  setup_brokerage: 0,
  setup_interior: 1200000,
  setup_cleaning: 150000,
  msg_contract: '', msg_checkin: '', msg_checkout_prev: '', msg_checkout_day: '',
  bookings: [
    {
      id: 'demo_b1', guest_name: '김민준', guest_phone: '010-1234-5678',
      channel: '에어비앤비', booking_type: '일반',
      check_in: '2026-02-10', check_out: '2026-02-24', days: 14,
      total_rent: 560000, total_maintenance: 160000, total_cleaning: 30000,
      booking_weekly_rent: 280000, booking_weekly_maint: 80000, booking_weekly_clean: 30000,
      cleaning_expense: 0, discount: 0, platform_fee: 24750,
      total_price: 750000, negotiated_price: 0, final_price: 725250,
      deposit: 330000, status: 'confirmed', payment_status: 'completed',
      deposit_refund_status: 'pending', msg_sent: {}, created_at: '2026-01-20T10:00:00',
    },
    {
      id: 'demo_b2', guest_name: '이서연', guest_phone: '010-9876-5432',
      channel: '직거래', booking_type: '일반',
      check_in: '2026-03-01', check_out: '2026-03-29', days: 28,
      total_rent: 1120000, total_maintenance: 320000, total_cleaning: 30000,
      booking_weekly_rent: 280000, booking_weekly_maint: 80000, booking_weekly_clean: 30000,
      cleaning_expense: 0, discount: 50000, platform_fee: 0,
      total_price: 1470000, negotiated_price: 0, final_price: 1420000,
      deposit: 330000, status: 'confirmed', payment_status: 'pending',
      deposit_refund_status: 'pending', msg_sent: {}, created_at: '2026-02-01T10:00:00',
    },
  ],
  blocked_dates: [],
  monthly_expenses: { '2026-02': { maintenance_fee: 650000 } },
  created_at: '2026-01-01T00:00:00',
  updated_at: '2026-01-01T00:00:00',
  is_collaborator: false,
  owner_name: '',
  collaborators: [],
};

export default function RoomManagement({ token, user }: RoomManagementProps) {
  const { isMobile } = useResponsive();
  // Data
  const [rooms, setRooms] = useState<STRRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentRoom, setCurrentRoom] = useState<STRRoom | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const currentRoomIdRef = useRef<string | null>(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  // Calendar
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [selStart, setSelStart] = useState<Date | null>(null);
  const [selEnd, setSelEnd] = useState<Date | null>(null);

  // Modals
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [editRoom, setEditRoom] = useState<STRRoom | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editBooking, setEditBooking] = useState<STRBooking | null>(null);
  const [isExtension, setIsExtension] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editBlockData, setEditBlockData] = useState<{ start: string; end: string; reason: string; index: number } | null>(null);
  const [showMessageModal, setShowMessageModal] = useState<string | false>(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelBooking, setCancelBooking] = useState<STRBooking | null>(null);
  const [showCoManageModal, setShowCoManageModal] = useState(false);
  const [coManageRoom, setCoManageRoom] = useState<STRRoom | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: string } | null>(null);

  const showToast = (msg: string, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Sync ref with currentRoom to avoid infinite loop in fetchRooms
  useEffect(() => { currentRoomIdRef.current = currentRoom?.id || null; }, [currentRoom]);

  // Fetch rooms
  const fetchRooms = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setRooms(data);
        if (currentRoomIdRef.current) {
          const updated = data.find((r: STRRoom) => r.id === currentRoomIdRef.current);
          if (updated) setCurrentRoom(updated);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  // Room CRUD
  const handleRoomSave = async (data: any) => {
    try {
      const url = editRoom
        ? `${API_BASE_URL}/api/str-rooms/${editRoom.id}`
        : `${API_BASE_URL}/api/str-rooms`;
      const res = await fetch(url, {
        method: editRoom ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        showToast(editRoom ? '방이 수정되었습니다' : '방이 추가되었습니다');
        setShowRoomModal(false);
        setEditRoom(null);
        if (isDemoMode) setIsDemoMode(false);  // 실제 방 등록 시 데모 종료
        await fetchRooms();
      } else {
        const err = await res.json();
        showToast(err.detail || '저장 실패', 'error');
      }
    } catch (e) { showToast('서버 오류', 'error'); }
  };

  // 데모 모드 핸들러
  const handleShowDemo = () => {
    setIsDemoMode(true);
    setCurrentRoom(DEMO_ROOM);
    if (isMobile) setMobileDetailOpen(true);
  };

  const handleExitDemo = () => {
    setIsDemoMode(false);
    setCurrentRoom(null);
  };

  const handleDeleteRoom = async (room: STRRoom) => {
    if (!window.confirm(`'${room.name}' 방을 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        showToast('방이 삭제되었습니다');
        if (currentRoom?.id === room.id) setCurrentRoom(null);
        await fetchRooms();
      }
    } catch (e) { showToast('삭제 실패', 'error'); }
  };

  // Calendar nav
  const calNav = (delta: number) => {
    let nm = calMonth + delta;
    let ny = calYear;
    if (nm < 0) { nm = 11; ny--; }
    if (nm > 11) { nm = 0; ny++; }
    setCalYear(ny);
    setCalMonth(nm);
  };

  const handleClickDate = (dateStr: string) => {
    const dt = new Date(dateStr + 'T00:00:00');
    if (!selStart || selEnd) {
      setSelStart(dt);
      setSelEnd(null);
    } else {
      if (dt <= selStart) {
        setSelStart(dt);
      } else {
        setSelEnd(dt);
      }
    }
  };

  // Booking CRUD
  const handleBookingSave = async (data: any, isEdit: boolean) => {
    if (!currentRoom) return;
    try {
      const url = isEdit
        ? `${API_BASE_URL}/api/str-rooms/${currentRoom.id}/bookings/${editBooking?.id}`
        : `${API_BASE_URL}/api/str-rooms/${currentRoom.id}/bookings`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        const updated = await res.json();
        showToast(isEdit ? '예약이 수정되었습니다' : '예약이 추가되었습니다');
        setShowBookingModal(false);
        setEditBooking(null);
        setCurrentRoom(updated);
        setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setSelStart(null);
        setSelEnd(null);
      } else {
        const err = await res.json();
        showToast(err.detail || '저장 실패', 'error');
      }
    } catch (e) { showToast('서버 오류', 'error'); }
  };

  const handleCancelBooking = async (action: 'cancel' | 'delete') => {
    if (!currentRoom || !cancelBooking) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${currentRoom.id}/bookings/${cancelBooking.id}?action=${action}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        showToast(action === 'delete' ? '예약이 삭제되었습니다' : '예약이 취소되었습니다');
        setShowCancelModal(false);
        setCancelBooking(null);
        await fetchRooms();
      }
    } catch (e) { showToast('처리 실패', 'error'); }
  };

  // Blocked dates
  const handleBlockSave = async (start: string, end: string, reason: string) => {
    if (!currentRoom) return;
    const blocked = [...(currentRoom.blocked_dates || [])];
    if (editBlockData !== null && editBlockData.index >= 0) {
      blocked[editBlockData.index] = { ...blocked[editBlockData.index], start, end, reason };
    } else {
      blocked.push({ id: 0, start, end, reason });
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${currentRoom.id}/blocked-dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ blocked_dates: blocked.map((bl) => ({ start: bl.start, end: bl.end, reason: bl.reason })) }),
      });
      if (res.ok) {
        const updated = await res.json();
        showToast('저장되었습니다');
        setShowBlockModal(false);
        setEditBlockData(null);
        setCurrentRoom(updated);
        setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setSelStart(null);
        setSelEnd(null);
      }
    } catch (e) { showToast('저장 실패', 'error'); }
  };

  const handleUnblock = async () => {
    if (!currentRoom || !selStart || !selEnd) return;
    const startStr = formatDate(selStart);
    const endStr = formatDate(selEnd);
    const blocked = (currentRoom.blocked_dates || []).filter((bl) => {
      return !(bl.start >= startStr && bl.end <= endStr);
    });
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${currentRoom.id}/blocked-dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ blocked_dates: blocked.map((bl) => ({ start: bl.start, end: bl.end, reason: bl.reason })) }),
      });
      if (res.ok) {
        const updated = await res.json();
        showToast('예약불가가 해제되었습니다');
        setCurrentRoom(updated);
        setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setSelStart(null);
        setSelEnd(null);
      }
    } catch (e) { showToast('처리 실패', 'error'); }
  };

  const handleDeleteBlock = async (index: number) => {
    if (!currentRoom || !window.confirm('이 예약불가를 삭제하시겠습니까?')) return;
    const blocked = (currentRoom.blocked_dates || []).filter((_, i) => i !== index);
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${currentRoom.id}/blocked-dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ blocked_dates: blocked.map((bl) => ({ start: bl.start, end: bl.end, reason: bl.reason })) }),
      });
      if (res.ok) {
        const updated = await res.json();
        setCurrentRoom(updated);
        setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        showToast('삭제되었습니다');
      }
    } catch (e) { showToast('삭제 실패', 'error'); }
  };

  // Messages
  const handleMessagesSave = async (messages: Record<string, string>) => {
    if (!currentRoom) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${currentRoom.id}/messages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(messages),
      });
      if (res.ok) {
        showToast('메시지가 저장되었습니다');
        setShowMessageModal(false);
        setCurrentRoom((prev) => prev ? { ...prev, ...messages } : prev);
        setRooms((prev) => prev.map((r) => (r.id === currentRoom.id ? { ...r, ...messages } : r)));
      }
    } catch (e) { showToast('저장 실패', 'error'); }
  };

  const handleRoomUpdated = (updated: STRRoom) => {
    setCurrentRoom(updated);
    setRooms((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  // 데모 모드: 방이 없을 때만 데모방 주입
  const displayRooms = isDemoMode && rooms.length === 0 ? [DEMO_ROOM] : rooms;

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: '#fff', padding: '16px 0 0' }}>
      {/* 데모 모드 배너 */}
      {isDemoMode && !(isMobile && mobileDetailOpen) && (
        <div style={{
          background: '#fff8e1', border: '1px solid #ffd54f', borderRadius: 8,
          padding: '10px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: 12, fontSize: 13,
        }}>
          <span>📌 <b>예시 매물</b>입니다. 실제 방을 등록하면 사라집니다.</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setEditRoom(null); setShowRoomModal(true); }}
              style={{ padding: '4px 12px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
            >
              지금 등록하기
            </button>
            <button
              onClick={handleExitDemo}
              style={{ padding: '4px 10px', background: 'none', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#666' }}
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {/* Header row: 공동관리 새로고침 + 방 정보 입력 */}
      <div style={{ display: isMobile && mobileDetailOpen ? 'none' : 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {rooms.some(r => r.is_collaborator) && (
          <button
            onClick={() => { fetchRooms(); showToast('최신 데이터로 새로고침되었습니다'); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: isMobile ? 4 : 6,
              padding: isMobile ? '6px 12px' : '8px 16px', color: '#fff', border: 'none', borderRadius: isMobile ? 6 : 8,
              cursor: 'pointer', fontSize: isMobile ? 12 : 13, fontWeight: 500,
              background: '#ff9800',
            }}
          >
            공동관리 새로고침
          </button>
        )}
        <button
          onClick={() => { setEditRoom(null); setShowRoomModal(true); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: isMobile ? 4 : 6, padding: isMobile ? '6px 12px' : '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: isMobile ? 6 : 8, cursor: 'pointer', fontSize: isMobile ? 12 : 13, fontWeight: 500 }}
        >
          + 방 정보 입력
        </button>
      </div>

      {/* Room list */}
      <div style={{ display: isMobile && mobileDetailOpen ? 'none' : 'block' }}>
        <RoomList
          rooms={displayRooms}
          loading={loading}
          currentRoom={currentRoom}
          onSelect={(room) => { setCurrentRoom(room); setSelStart(null); setSelEnd(null); if (isMobile) setMobileDetailOpen(true); }}
          onEdit={(room) => { if (room.id === '__demo__') return; setEditRoom(room); setShowRoomModal(true); }}
          onDelete={(room) => { if (room.id === '__demo__') return; handleDeleteRoom(room); }}
          onCoManage={(room) => { if (room.id === '__demo__') return; setCoManageRoom(room); setShowCoManageModal(true); }}
          onAddRoom={() => { setEditRoom(null); setShowRoomModal(true); }}
          onShowDemo={handleShowDemo}
        />
      </div>

      {/* Room detail — wrapped in a single card like c1 */}
      {currentRoom && (!isMobile || mobileDetailOpen) && (
        <div style={{ marginTop: isMobile && mobileDetailOpen ? 0 : 16, background: '#fff', border: '1px solid #e2e5e9', borderRadius: 12, overflow: 'hidden' }}>
          {/* Card header: room name + type + message template buttons (c1: st-cal-header) */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', padding: isMobile ? '12px 14px' : '16px 20px', borderBottom: '1px solid #e2e5e9', gap: isMobile ? 10 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12, minWidth: 0 }}>
              {isMobile && mobileDetailOpen && (
                <button
                  onClick={() => setMobileDetailOpen(false)}
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '2px 4px', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 13, color: '#1976d2', fontWeight: 500, flexShrink: 0,
                  }}
                >
                  ‹ 목록
                </button>
              )}
              <span style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{currentRoom.name}</span>
              <span style={{ fontSize: 11, color: '#888', flexShrink: 0, whiteSpace: 'nowrap' }}>{currentRoom.room_type}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                { label: '계약 확정 & 이용 정보', key: 'msg_contract' },
                { label: '입실 정보', key: 'msg_checkin' },
                { label: '퇴실 1일전', key: 'msg_checkout_prev' },
                { label: '퇴실 당일', key: 'msg_checkout_day' },
              ].map((btn) => (
                <button
                  key={btn.key}
                  onClick={() => setShowMessageModal(btn.key)}
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: isMobile ? '6px 10px' : '4px 10px', fontSize: 11, fontWeight: 500,
                    border: 'none', borderRadius: 8,
                    background: '#f5f5f5', color: '#333',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          {/* Card body: Calendar + Sidebar */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
            {/* Left: Calendar */}
            <div style={{ flex: 1, minWidth: 0, padding: isMobile ? 10 : 16 }}>
              <Calendar
                room={currentRoom}
                year={calYear}
                month={calMonth}
                selStart={selStart}
                selEnd={selEnd}
                onClickDate={handleClickDate}
                onNav={calNav}
              />
            </div>

            {/* Right: Settlement + Date selection (sidebar) */}
            <div style={{
              width: isMobile ? '100%' : 280,
              borderLeft: isMobile ? 'none' : '1px solid #e2e5e9',
              borderTop: isMobile ? '1px solid #e2e5e9' : 'none',
              background: '#fafafa', display: 'flex', flexDirection: 'column', alignSelf: 'stretch',
            }}>
              {/* 모바일: Sidebar(액션 버튼)를 먼저 → 달력 바로 아래 */}
              {isMobile && (
                <div style={{ padding: '12px 14px 16px', borderBottom: '1px solid #e2e5e9' }}>
                  <Sidebar
                    room={currentRoom}
                    selStart={selStart}
                    selEnd={selEnd}
                    onOpenBookingModal={(ext) => { setEditBooking(null); setIsExtension(ext); setShowBookingModal(true); }}
                    onOpenBlockModal={() => { setEditBlockData(null); setShowBlockModal(true); }}
                    onUnblock={handleUnblock}
                    onClearSelection={() => { setSelStart(null); setSelEnd(null); }}
                  />
                </div>
              )}
              <div style={{ padding: '12px 14px 4px', flexShrink: 0 }}>
                <SettlementDashboard room={currentRoom} token={token} onRoomUpdated={handleRoomUpdated} />
              </div>
              {/* 데스크톱: Sidebar는 Settlement 아래 (기존 위치) */}
              {!isMobile && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 16px', borderTop: '1px solid #e2e5e9' }}>
                  <Sidebar
                    room={currentRoom}
                    selStart={selStart}
                    selEnd={selEnd}
                    onOpenBookingModal={(ext) => { setEditBooking(null); setIsExtension(ext); setShowBookingModal(true); }}
                    onOpenBlockModal={() => { setEditBlockData(null); setShowBlockModal(true); }}
                    onUnblock={handleUnblock}
                    onClearSelection={() => { setSelStart(null); setSelEnd(null); }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Booking list — inside the card */}
          <div style={{ padding: '0 16px 16px' }}>
            <BookingList
              room={currentRoom}
              token={token}
              onRoomUpdated={handleRoomUpdated}
              onEditBooking={(b) => { setEditBooking(b); setIsExtension(b.booking_type === '직거래 연장'); setShowBookingModal(true); }}
              onCancelBooking={(b) => { setCancelBooking(b); setShowCancelModal(true); }}
              onEditBlock={(idx) => {
                const bl = currentRoom.blocked_dates[idx];
                setEditBlockData({ start: bl.start, end: bl.end, reason: bl.reason, index: idx });
                setShowBlockModal(true);
              }}
              onDeleteBlock={handleDeleteBlock}
            />
          </div>
        </div>
      )}

      {/* Modals */}
      {showRoomModal && (
        <RoomModal room={editRoom} onSave={handleRoomSave} onClose={() => { setShowRoomModal(false); setEditRoom(null); }} />
      )}
      {showBookingModal && currentRoom && (
        <BookingModal
          room={currentRoom}
          booking={editBooking}
          isExtension={isExtension}
          selStart={selStart}
          selEnd={selEnd}
          onSave={handleBookingSave}
          onClose={() => { setShowBookingModal(false); setEditBooking(null); }}
        />
      )}
      {showBlockModal && (
        <BlockModal
          selStart={selStart}
          selEnd={selEnd}
          editData={editBlockData}
          onSave={handleBlockSave}
          onClose={() => { setShowBlockModal(false); setEditBlockData(null); }}
        />
      )}
      {showMessageModal && currentRoom && (
        <MessageModal room={currentRoom} initialTab={showMessageModal || undefined} onSave={handleMessagesSave} onClose={() => setShowMessageModal(false)} />
      )}
      {showCancelModal && cancelBooking && (
        <CancelModal
          booking={cancelBooking}
          onCancel={() => handleCancelBooking('cancel')}
          onDelete={() => handleCancelBooking('delete')}
          onClose={() => { setShowCancelModal(false); setCancelBooking(null); }}
        />
      )}

      {showCoManageModal && coManageRoom && (
        <CoManageModal
          room={coManageRoom}
          token={token}
          onClose={() => { setShowCoManageModal(false); setCoManageRoom(null); }}
          onUpdated={fetchRooms}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '10px 20px',
          background: toast.type === 'error' ? '#c62828' : '#2e7d32',
          color: 'white',
          borderRadius: 8,
          fontSize: 14,
          zIndex: 2000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
